import { useAuthContext } from 'contexts/AuthContext';
import FHIRFetcher from 'data/FHIRFetcher';
import FoodFetcher from 'data/FoodFetcher';
import type { Contract } from 'fhir/r4';
import { useEffect, useState } from 'react';
import type { DropdownProps, MenuItemProps } from 'semantic-ui-react';
import {
  Popup,
  Segment,
  Menu,
  Dropdown,
  Label,
  Container,
  Checkbox,
  List,
} from 'semantic-ui-react';
import useSWR from 'swr';
import type { Fetched, FoodOrder, Option, ProgramBudget, ReferralSummary } from 'types';
import { setProgramBudgetByReferralId } from 'utils';
import { parseFHIRContract } from 'utils/FHIRUtils';

import Catalog from '../../common/food/catalog';
import PatientOrders from '../../common/food/orders';
import { PROGRAMS_BUDGET_MAP, PROGRAMS_MEQ } from '../vendorConfig';

import DraftOrders from './draftOrders';
import EBTCard from './ebtCard';
import Feedback from './feedback';
import FoodRecommendations from './foodRecommendations';
import Invoices from './invoices';
import { calculateMealEquivalents, calculateMealPricing } from './utils/foodUtils';

/**
 * What the food tab accepts. Every field is forwarded to `PatientOrders`, so the
 * value types are taken from that component's props.
 *
 * Two modes: `patient/index.tsx` supplies member mode (real `patientId`,
 * `patient`, `profileData`, `allReferralData`, `programBudget`);
 * `home/index.tsx` supplies roster-wide mode with `patientId={null}`,
 * `org={null}` and none of the rest.
 *
 * Each is `?: T | undefined` rather than `?: T` because both call sites pass
 * several as JSX attributes holding absent values, which under
 * `exactOptionalPropertyTypes` is a pass of `undefined`, not an omission.
 * `org` additionally admits `null`; the read below already guards with `?.`.
 */
type PatientOrdersProps = React.ComponentProps<typeof PatientOrders>;

type FoodProps = {
  [K in
    | 'allReferralData'
    | 'onPatientSelected'
    | 'patient'
    | 'patientId'
    | 'profileData'
    | 'programBudget'
    | 'setProgramBudget']?: PatientOrdersProps[K] | undefined;
} & {
  org?: string | null | undefined;
};

function Food(props: FoodProps) {
  const [invoicesOnly, setInvoicesOnly] = useState(false);
  const [feedbackOnly, setFeedbackOnly] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(props.patientId ? 'All' : 'PK');
  const [selectedVendorName, setSelectedVendorName] = useState(
    props.patientId ? 'All' : 'Performance Kitchen',
  );
  const [selectedOrg, setSelectedOrg] = useState('All');
  const DISPLAY_ALL_VENDORS = props.patientId && selectedVendor === 'All';
  const [catalogOnly, setCatalogOnly] = useState(DISPLAY_ALL_VENDORS ? false : true);
  const [ordersOnly, setOrdersOnly] = useState(DISPLAY_ALL_VENDORS ? true : false);
  const [draftOrdersOnly, setDraftOrdersOnly] = useState(DISPLAY_ALL_VENDORS ? false : true);
  const [foodRecommendationsOnly, setFoodRecommendationsOnly] = useState(
    DISPLAY_ALL_VENDORS ? false : true,
  );
  const [activeItem, setActiveItem] = useState(DISPLAY_ALL_VENDORS ? 'Orders' : 'Catalog');
  const [vendorsOptions, setVendorsOptions] = useState<Option[]>([]);
  /*
   * Both are keyed by org id off `parseFHIRContract`, so both key sets are open.
   * The value is `string | number` because that is what `DraftOrders` and
   * `FoodRecommendations` declare, and `FlatContract` types the source field
   * `unknown` — nothing narrows it to a number on the way in.
   */
  const [contractsME, setContractsME] = useState<Record<string, string | number>>({});
  const [_contractMaxBudget, setContractsMaxBudget] = useState<Record<string, string | number>>({});
  const [ebtCardOnly, setEbtCardOnly] = useState(false);
  const [ordersMealEquivalents, setOrdersMealEquivalents] = useState(0);
  const [programOrdersCost, setProgramOrdersCost] = useState(0);
  const [programMAXMealEquivalents, setProgramMAXMealEquivalents] = useState(0);
  const [programMAXBudget, setProgramMAXBudget] = useState<number | undefined>(0);
  const [updatedProgramsBudget, setUpdatedProgramsBudget] = useState<ProgramBudget[]>([]);
  const [budgetOverrideChecked, setBudgetOverrideChecked] = useState(false);
  const [_referralId, setReferralId] = useState<string | null | undefined>(null);
  const [_programHouseHoldSize, _setProgramHouseHoldSize] = useState('');
  const [_programBudgetLabel, _setProgramBudgetLabel] = useState('');
  const authInfo = useAuthContext();
  const foodCardVendorIds = (authInfo.foodCardVendors ?? []).map(
    (vendor: { value?: string }) => vendor.value,
  );

  useEffect(() => {
    setVendorsOptions(authInfo.vendors);
  }, [authInfo]);

  useEffect(() => {
    if (
      (props.allReferralData?.length as number) > 0 &&
      (props.programBudget?.length as number) > 0
    ) {
      const inProgressReferrals = props.allReferralData!.filter(
        (item: ReferralSummary) => item.status?.toUpperCase() === 'IN-PROGRESS',
      );

      if (inProgressReferrals?.length > 0) {
        // Guarded by the length check immediately above.
        const activeReferral = inProgressReferrals[0]!;
        setReferralId(activeReferral?.referralId);

        // Enrich programsBudget with status. Annotated because the `map` returns
        // a union of the untouched rows and the spread-plus-status ones, which is
        // wider than `ProgramBudget[]` only in that its `status` is
        // `string | undefined` — the same thing `ProgramBudget` already declares.
        const updatedProgramsBudget: ProgramBudget[] = props.programBudget!.map(
          (budget: ProgramBudget): ProgramBudget => {
            if (budget.referralId === activeReferral.referralId) {
              return {
                ...budget,
                status: activeReferral.status!, // <-- added here
              };
            }
            return budget;
          },
        );

        setUpdatedProgramsBudget(updatedProgramsBudget);

        // Loop through array and find the matching referralId
        const matched = updatedProgramsBudget.find(
          (b: ProgramBudget) => b.referralId === activeReferral.referralId,
        );
        //let matched = props.programBudget.filter((resource) => resource.referralId === referralData[0]?.referralId);
        console.log('programFoodBudget', matched?.programFoodBudget);

        /*
         * SUSPECTED DEFECT (the line below) — REPORTED, NOT FIXED.
         * `matched?.programFoodBudget` is `undefined` on two paths: when no budget
         * row matches the active referral, and when the matched row has no
         * `programFoodBudget` ({@link ProgramBudget} declares it optional). The
         * budget cap then holds `undefined` rather than `0`, and the over-budget
         * comparisons downstream all become `x > undefined`, i.e. false — so the
         * budget guard silently stops firing instead of clamping to zero, and an
         * order above the contractual ceiling submits without the warning.
         * The state is declared `number | undefined` so the type says what the
         * runtime does; `?? 0` here would instead change which orders are allowed
         * through, which is why it is reported rather than fixed.
         */
        // Return the programFoodBudget if found
        setProgramMAXBudget(matched?.programFoodBudget);
      }
    }
  }, [props.allReferralData, props.programBudget]);

  const handleMenuItemClick = (e: React.MouseEvent<HTMLAnchorElement>, { name }: MenuItemProps) => {
    setActiveItem(name!);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type
    name === 'Catalog' ? setCatalogOnly(true) : setCatalogOnly(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type
    name === 'Orders' ? setOrdersOnly(true) : setOrdersOnly(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type
    name === 'DraftOrders' ? setDraftOrdersOnly(true) : setDraftOrdersOnly(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type
    name === 'FoodRecommendations'
      ? setFoodRecommendationsOnly(true)
      : setFoodRecommendationsOnly(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type
    name === 'Invoices' ? setInvoicesOnly(true) : setInvoicesOnly(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type
    name === 'Feedback' ? setFeedbackOnly(true) : setFeedbackOnly(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- ternary used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type
    name === 'EBT Card' ? setEbtCardOnly(true) : setEbtCardOnly(false);
  };

  const handleVendorSelection = (
    e: React.SyntheticEvent<HTMLElement>,
    { value }: DropdownProps,
  ) => {
    setSelectedVendor(value as string);
    // Reads the clicked option's rendered label off the DOM rather than looking
    // it up in `vendorsOptions`. `EventTarget` has no `textContent`, so the cast
    // states what the handler already relies on.
    setSelectedVendorName((e.target as HTMLElement).textContent);
  };
  const handleOrgSelection = (e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    setSelectedOrg(value as string);
  };

  const orderOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (orderData: Fetched<FoodOrder[]>) => {
      if ((orderData?.length as number) > 0) {
        let tempMealEquivalents = 0;
        let tempFoodPricing = 0;

        for (const order of orderData!) {
          // Neither total is read again; the accumulation is upstream's and stays.
          /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
          tempMealEquivalents += calculateMealEquivalents(order, false, props);
          tempFoodPricing += calculateMealPricing(order, false, props);
        }

        tempFoodPricing = Math.round(tempFoodPricing * 100) / 100; // Round to 2 decimal places

        if (props.programBudget && props.programBudget.length > 0) {
          setProgramBudgetByReferralId(
            orderData![0]?.referralId,
            tempFoodPricing,
            props.programBudget,
            props.setProgramBudget!,
          );
        }
      }
    },
  };

  const handleItemCheck = (_event: unknown, _data: unknown, _quantity: unknown) => {
    setBudgetOverrideChecked(!budgetOverrideChecked);
  };

  const programContractOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    /*
     * `maxMealEquivalents` and `maxBudget` are `unknown` on {@link FlatContract}
     * (fhirpath collections), so both accumulators are `string` to `unknown` and
     * the read below is asserted to the `number` its `useState(0)` consumer
     * expects.
     *
     * `contract.name` is `string | undefined`, so an unnamed contract writes a
     * literal `'undefined'` key rather than being skipped. Recorded, not guarded.
     */
    onSuccess: (contracts: Fetched<{ entry: { resource: Contract }[] }>) => {
      const tempContractsME: Record<string, unknown> = {};
      const tempContractsBudget: Record<string, unknown> = {};
      contracts!.entry.forEach((entry: { resource: Contract }) => {
        const contract = parseFHIRContract(entry.resource);
        tempContractsME[contract.name!] = contract?.maxMealEquivalents;
        tempContractsBudget[contract.name!] = contract?.maxBudget;
      });
      setContractsME(tempContractsME as Record<string, string | number>);
      setContractsMaxBudget(tempContractsBudget as Record<string, string | number>);

      if (Object.prototype.hasOwnProperty.call(tempContractsME, props.org!)) {
        setProgramMAXMealEquivalents(tempContractsME[props.org!] as number);
      }
      // if (tempContractsBudget.hasOwnProperty(props.org)) {
      //   setProgramMAXBudget(tempContractsBudget[props.org]);
      // }
    },
  };

  const { data: ordersData, mutate: ordersMutate } = useSWR<Fetched<FoodOrder[]>, Error>(
    props.patientId
      ? [`/order?patientId=` + props.patientId + '&foodTypeCode:ne=FC', authInfo.token]
      : null,
    FoodFetcher,
    orderOptions,
  );

  const { data: _programContract, mutate: _programContractMutate } = useSWR(
    // [`/Contract?_id=` + props.org, authInfo.token],
    [`/Contract`, authInfo.token],
    FHIRFetcher,
    programContractOptions,
  );

  // We want to exclude INTAKE referrals from budget display (for EW-PAAS-MA program)
  const isValidReferral = (referralId: string) => !referralId.toUpperCase().includes('INTAKE');

  // All three admit `null` because the Solutran views clear them with an
  // explicit `setX(null)` — see `ebtCard/solutran/makePayment.tsx:144-145`.
  const [solutranView, setSolutranView] = useState<'makePayment' | 'pastPayments' | null>(); //makePayment, pastPayments
  const [solutranMakePaymentSelectedOrder, setSolutranMakePaymentSelectedOrder] = useState<
    string | null
  >();
  const [solutranPastPaymentsSelectedOrder, setSolutranPastPaymentsSelectedOrder] = useState<
    string | null
  >();

  const openSolutranView = (view: 'makePayment' | 'pastPayments', order?: string) => {
    setSolutranView(view);
    if (view === 'makePayment') {
      setSolutranMakePaymentSelectedOrder(order);
    } else if (view === 'pastPayments') {
      setSolutranPastPaymentsSelectedOrder(order);
    }

    setActiveItem('EBT Card');
    setCatalogOnly(false);
    setOrdersOnly(false);
    setInvoicesOnly(false);
    setFeedbackOnly(false);
    setEbtCardOnly(true);
  };

  return (
    <div>
      <Menu pointing>
        {!props.patientId && (
          <Popup
            flowing
            trigger={
              <Dropdown
                scrolling
                className="link item"
                style={{ zIndex: '99999' }}
                onChange={handleOrgSelection}
                options={
                  !props.patientId
                    ? [{ text: 'All', key: 'All', value: 'All' }].concat(authInfo.payers)
                    : authInfo.payers
                }
                defaultValue={selectedOrg}
              />
            }
            content="Payer Organization"
            on="hover"
          />
        )}
        <Popup
          flowing
          trigger={
            <Dropdown
              scrolling
              className="link item"
              onChange={handleVendorSelection}
              options={[{ text: 'All', key: 'All', value: 'All' }].concat(vendorsOptions)}
              defaultValue={selectedVendor}
            />
          }
          content="Vendors"
          on="hover"
        />
        {selectedVendor !== 'All' && (
          <Menu.Item
            key="catalog"
            name="Catalog"
            active={activeItem === 'Catalog'}
            onClick={handleMenuItemClick}
          />
        )}
        <Menu.Item
          key="orders"
          name="Orders"
          active={activeItem === 'Orders'}
          onClick={handleMenuItemClick}
        >
          {'Orders'}
          {PROGRAMS_MEQ.includes(props.org!) && props.patientId && (
            <Label floating as="a" basic color="blue" size="medium">
              {ordersMealEquivalents}/{programMAXMealEquivalents}
            </Label>
          )}
        </Menu.Item>

        <Menu.Item
          key="draftOrders"
          name="DraftOrders"
          active={activeItem === 'DraftOrders'}
          onClick={handleMenuItemClick}
        />
        <Menu.Item
          key="foodRecommendations"
          name="FoodRecommendations"
          active={activeItem === 'FoodRecommendations'}
          onClick={handleMenuItemClick}
        />

        {props.patientId === null && (
          <Menu.Item
            key="Invoices"
            name="Invoices"
            active={activeItem === 'Invoices'}
            onClick={handleMenuItemClick}
          />
        )}
        {props.patientId !== null && (
          <Menu.Item
            key="Feedback"
            name="Feedback"
            active={activeItem === 'Feedback'}
            onClick={handleMenuItemClick}
          />
        )}
        {props.patientId !== null && (
          <Menu.Item
            key="EBT Card"
            name="EBT Card"
            active={activeItem === 'EBT Card'}
            onClick={handleMenuItemClick}
          />
        )}
        <Menu.Item key="Budget" name="Budget">
          {PROGRAMS_BUDGET_MAP?.[props?.org?.toUpperCase() as string]?.ACTIVE &&
            props.patientId && (
              <>
                <List horizontal>
                  {updatedProgramsBudget &&
                    updatedProgramsBudget
                      ?.filter((budget) => isValidReferral(budget.referralId!))
                      ?.map((budget) => {
                        const referralId = budget?.referralId || '';
                        const org = props.org || '';

                        // Determine what to display:
                        const displayReferral = referralId.startsWith(org)
                          ? referralId.substring(org.length + 1) // strip org prefix
                          : referralId; // show full id if org is not a subset

                        return (
                          budget?.['status'] === 'IN-PROGRESS' && (
                            <List.Item key={referralId}>
                              <Label
                                basic
                                color="red"
                                size="medium"
                                style={{ marginBottom: '-4px' }}
                              >
                                {`${displayReferral}: $${budget.programOrderCost} / $${budget.programFoodBudget}`}
                              </Label>
                            </List.Item>
                          )
                        );
                      })}
                </List>
              </>
            )}
        </Menu.Item>
        <Menu.Item key="BudgetOverride" name="BudgetOverride">
          {PROGRAMS_BUDGET_MAP?.[props?.org?.toUpperCase() as string]?.ACTIVE &&
            props.patientId && (
              <>
                <Checkbox
                  color="blue"
                  // checked={checkedItem}
                  id="budgetOverride"
                  floated="left"
                  label="Budget Override"
                  style={{
                    backgroundColor: budgetOverrideChecked ? '#e6f3ff' : 'transparent',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    border: budgetOverrideChecked ? '2px solid #2185d0' : '2px solid transparent',
                    color: '#2185d0',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                  onClick={(event, data) => handleItemCheck(event, data, 'budgetOverride')}
                />
              </>
            )}
        </Menu.Item>
      </Menu>
      <Segment>
        {selectedVendor && selectedVendor.length > 0 && catalogOnly && (
          <Catalog
            patient={props.patient}
            vendor={selectedVendor}
            handleMenuItemClick={handleMenuItemClick}
            patientId={props.patientId}
            profileData={props.profileData}
            org={props.org}
            ordersMealEquivalents={ordersMealEquivalents}
            setOrdersMealEquivalents={setOrdersMealEquivalents}
            setProgramOrdersCost={setProgramOrdersCost}
            programOrdersCost={programOrdersCost}
            programMAXMealEquivalents={programMAXMealEquivalents}
            programMAXBudget={programMAXBudget}
            setProgramMAXBudget={setProgramMAXBudget}
            allReferralData={props.allReferralData}
            budgetOverrideChecked={budgetOverrideChecked}
            setBudgetOverrideChecked={setBudgetOverrideChecked}
            ordersMutate={ordersMutate}
            programBudget={props.programBudget}
            setProgramBudget={props.setProgramBudget}
          />
        )}

        {selectedVendor && selectedVendor.length > 0 && ordersOnly && props.patientId !== null && (
          <PatientOrders
            profileData={props.profileData}
            patient={props.patient}
            patientId={props.patientId}
            vendor={selectedVendor}
            vendorName={selectedVendorName}
            selectedOrg={props.org}
            openSolutranView={openSolutranView as (view: string, orderId?: string) => void}
            ordersMealEquivalents={ordersMealEquivalents}
            setOrdersMealEquivalents={setOrdersMealEquivalents}
            setProgramOrdersCost={setProgramOrdersCost}
            programOrdersCost={programOrdersCost}
            programMAXMealEquivalents={programMAXMealEquivalents}
            programMAXBudget={programMAXBudget!}
            setProgramMAXBudget={setProgramMAXBudget}
            allPatientOrders={ordersData!}
            allReferralData={props.allReferralData}
            org={props.org}
            programBudget={props.programBudget}
            programBudget1={props.programBudget!}
            setProgramBudget={props.setProgramBudget}
            foodCardVendorIds={foodCardVendorIds as string[]}
          />
        )}
        {selectedVendor && selectedVendor.length > 0 && ordersOnly && props.patientId === null && (
          <PatientOrders
            profileData={props.profileData}
            selectedOrg={selectedOrg}
            vendor={selectedVendor}
            vendorName={selectedVendorName}
            onPatientSelected={props.onPatientSelected}
            ordersMealEquivalents={ordersMealEquivalents}
            setOrdersMealEquivalents={setOrdersMealEquivalents}
            setProgramOrdersCost={setProgramOrdersCost}
            programOrdersCost={programOrdersCost}
            programMAXBudget={programMAXBudget!}
            setProgramMAXBudget={setProgramMAXBudget}
            org={props.org}
            programBudget={props.programBudget}
            programBudget1={props.programBudget!}
            setProgramBudget={props.setProgramBudget}
            foodCardVendorIds={foodCardVendorIds as string[]}
          />
        )}
        {selectedVendor &&
          selectedVendor.length > 0 &&
          draftOrdersOnly &&
          props.patientId !== null && (
            <DraftOrders
              contractsME={contractsME}
              profileData={props.profileData!}
              patient={props.patient!}
              patientId={props.patientId!}
              selectedOrg={props.org!}
              vendor={selectedVendor}
              vendorName={selectedVendorName}
              onPatientSelected={props.onPatientSelected!}
              ordersMealEquivalents={ordersMealEquivalents}
              setOrdersMealEquivalents={setOrdersMealEquivalents}
              setProgramOrdersCost={setProgramOrdersCost}
              programOrdersCost={programOrdersCost}
              programMAXMealEquivalents={programMAXMealEquivalents}
              programMAXBudget={programMAXBudget!}
              allReferralData={props.allReferralData!}
              programBudget={props.programBudget!}
              setProgramBudget={props.setProgramBudget!}
            />
          )}

        {selectedVendor &&
          selectedVendor.length > 0 &&
          draftOrdersOnly &&
          props.patientId === null &&
          activeItem !== 'Catalog' &&
          (selectedOrg !== 'All' ? (
            <DraftOrders
              contractsME={contractsME}
              profileData={props.profileData!}
              selectedOrg={selectedOrg}
              vendor={selectedVendor}
              vendorName={selectedVendorName}
              onPatientSelected={props.onPatientSelected!}
              ordersMealEquivalents={ordersMealEquivalents}
              setOrdersMealEquivalents={setOrdersMealEquivalents}
              setProgramOrdersCost={setProgramOrdersCost}
              programOrdersCost={programOrdersCost}
              programMAXMealEquivalents={programMAXMealEquivalents}
              programMAXBudget={programMAXBudget!}
              programBudget={props.programBudget!}
              setProgramBudget={props.setProgramBudget!}
            />
          ) : (
            <Container textAlign="center">
              <Label basic color="red" size="large">
                Please select a payer organization
              </Label>
            </Container>
          ))}
        {selectedVendor &&
          selectedVendor.length > 0 &&
          foodRecommendationsOnly &&
          props.patientId === null &&
          activeItem !== 'Catalog' &&
          (selectedOrg !== 'All' ? (
            <FoodRecommendations
              contractsME={contractsME}
              profileData={props.profileData!}
              selectedOrg={selectedOrg}
              vendor={selectedVendor}
              vendorName={selectedVendorName}
              onPatientSelected={props.onPatientSelected!}
              ordersMealEquivalents={ordersMealEquivalents}
              setOrdersMealEquivalents={setOrdersMealEquivalents}
              programMAXMealEquivalents={programMAXMealEquivalents}
            />
          ) : (
            <Container textAlign="center">
              <Label basic color="red" size="large">
                Please select a payer organization
              </Label>
            </Container>
          ))}
        {selectedVendor &&
          selectedVendor.length > 0 &&
          foodRecommendationsOnly &&
          props.patientId !== null && (
            <FoodRecommendations
              contractsME={contractsME}
              profileData={props.profileData!}
              patient={props.patient!}
              patientId={props.patientId!}
              selectedOrg={props.org!}
              vendor={selectedVendor}
              vendorName={selectedVendorName}
              onPatientSelected={props.onPatientSelected!}
              ordersMealEquivalents={ordersMealEquivalents}
              setOrdersMealEquivalents={setOrdersMealEquivalents}
              programMAXMealEquivalents={programMAXMealEquivalents}
            />
          )}
        {selectedVendor &&
          selectedVendor.length > 0 &&
          invoicesOnly &&
          props.patientId === null && (
            <Invoices
              selectedOrg={selectedOrg}
              vendor={selectedVendor}
              onPatientSelected={props.onPatientSelected}
            />
          )}
        {selectedVendor &&
          selectedVendor.length > 0 &&
          feedbackOnly &&
          props.patientId !== null && (
            <Feedback
              selectedOrg={selectedOrg}
              vendor={selectedVendor}
              patientId={props.patientId}
            />
          )}
        {selectedVendor && selectedVendor.length > 0 && ebtCardOnly && props.patientId !== null && (
          <EBTCard
            ordersData={ordersData}
            ordersMutate={ordersMutate}
            org={props.org}
            solutranView={solutranView}
            setSolutranView={setSolutranView}
            solutranMakePaymentSelectedOrder={solutranMakePaymentSelectedOrder}
            setSolutranMakePaymentSelectedOrder={setSolutranMakePaymentSelectedOrder}
            solutranPastPaymentsSelectedOrder={solutranPastPaymentsSelectedOrder}
            setSolutranPastPaymentsSelectedOrder={setSolutranPastPaymentsSelectedOrder}
          />
        )}
      </Segment>
    </div>
  );
}

export default Food;
